import React, { useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IARPaymentHistoryDialogProps {
    isOpen: boolean;
    entryNo: string;
    onClose: () => void;
}

interface IArPaymentHistoryRow {
    id: string;
    paymentDate?: string;
    checkNo?: string;
    amount?: number;
    invoiceNo?: string;
    depositDate?: string;
    no?: string;
    entryNo?: string;
}

const PAGE_SIZE = 10;

const formatDateForDisplay = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '-';
    }

    return parsed.toLocaleDateString('en-US');
};

const formatCurrency = (value?: number | string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(parsed)) {
        return '-';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(parsed);
};

const ARPaymentHistoryDialog: React.FC<IARPaymentHistoryDialogProps> = ({ isOpen, entryNo, onClose }) => {
    const { get, response } = useFetch();

    const [rows, setRows] = useState<PagedResultSet<IArPaymentHistoryRow> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const columns: ITableColumn[] = useMemo(
        () => [
            { name: 'Payment Date', mapping: 'paymentDate', format: formatDateForDisplay },
            { name: 'Check No.', mapping: 'checkNo' },
            { name: 'Amount', mapping: 'amount', format: formatCurrency },
            { name: 'Invoice', mapping: 'invoiceNo' },
            { name: 'Deposit Date', mapping: 'depositDate', format: formatDateForDisplay },
            { name: 'No.', mapping: 'no' },
        ],
        []
    );

    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'id',
        columns,
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setPageNumber(1);
        setRequestError(null);
    }, [isOpen, entryNo]);

    useEffect(() => {
        if (!isOpen || !entryNo) {
            return;
        }

        const loadPaymentHistory = async (): Promise<void> => {
            setIsLoading(true);
            setRequestError(null);

            try {
                const skip = (pageNumber - 1) * PAGE_SIZE;
                const params = new URLSearchParams();
                params.append('skip', skip.toString());
                params.append('top', PAGE_SIZE.toString());
                params.append('entryNo', entryNo);

                const data = await get(`/v1.0/leases/admin/ar-invoice-history/payment-history?${params.toString()}`);

                if (!response.ok || !data) {
                    setRequestError(
                        parseLeaseRecordsApiError(response.data || { title: 'Failed to query payment history records.' }, response.status)
                    );
                    setRows(undefined);
                    return;
                }

                const items: IArPaymentHistoryRow[] = (data.items || []).map((row: IArPaymentHistoryRow, index: number) => ({
                    ...row,
                    id: `${entryNo}-${row.checkNo || ''}-${row.invoiceNo || ''}-${skip + index}`,
                }));

                const paged = new PagedResultSet<IArPaymentHistoryRow>();
                paged.items = items;
                paged.totalRecords = data.totalCount || 0;
                paged.pageNumber = pageNumber;
                paged.pageSize = PAGE_SIZE;
                setRows(paged);
            } catch (error) {
                setRequestError(parseLeaseRecordsApiError({ title: 'Error querying payment history records.' }, 500));
            } finally {
                setIsLoading(false);
            }
        };

        void loadPaymentHistory();
    }, [entryNo, get, isOpen, pageNumber, response]);

    const handlePageChange = (newPage: number): void => {
        setPageNumber(newPage);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            style={dialogStyles.overlay}
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <div style={dialogStyles.content} onClick={(e) => e.stopPropagation()}>
                <div style={dialogStyles.header}>
                    <h3 style={dialogStyles.title}>Payment History</h3>
                    <button onClick={onClose} style={dialogStyles.closeButton} aria-label="Close payment history dialog">
                        <X style={{ width: '0.95rem', height: '0.95rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={dialogStyles.body}>
                    <div style={dialogStyles.metaRow}>
                        <strong>Paid By / Entry No:</strong> <span>{entryNo || '-'}</span>
                    </div>

                    <div className="search-leases__table-wrapper ar-payment-history__table-wrapper" style={dialogStyles.tableSection}>
                        <Table isLoading={isLoading} tableConfig={tableConfig} data={rows} enablePaging={true} onPageChange={handlePageChange} />
                    </div>
                </div>

                <div style={dialogStyles.footer}>
                    <button onClick={onClose} style={dialogStyles.secondaryButton} disabled={isLoading}>
                        {isLoading ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                <Loader2 className="animate-spin" style={{ width: '0.9rem', height: '0.9rem' }} />
                                Loading...
                            </span>
                        ) : (
                            'Close'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        padding: '1rem',
    },
    content: {
        width: '92%',
        maxWidth: '860px',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        border: '1px solid #D1D5DB',
        boxShadow: '0 16px 35px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '70vh',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #E5E7EB',
        padding: '0.75rem 1rem',
        backgroundColor: '#F9FAFB',
    },
    title: {
        margin: 0,
        color: '#111827',
        fontSize: '0.95rem',
        fontWeight: 600,
    },
    closeButton: {
        color: '#6B7280',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        padding: '0.75rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6rem',
        minHeight: 0,
        overflow: 'hidden',
    },
    metaRow: {
        fontSize: '0.82rem',
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '0.45rem',
    },
    tableSection: {
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '44vh',
        minHeight: '190px',
    },
    footer: {
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
        padding: '0.65rem 1rem',
        display: 'flex',
        justifyContent: 'flex-end',
    },
    secondaryButton: {
        border: '1px solid #D1D5DB',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        borderRadius: '6px',
        padding: '0.5rem 0.9rem',
        fontWeight: 500,
        cursor: 'pointer',
        minWidth: '100px',
    },
};

export default ARPaymentHistoryDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ARPaymentHistoryDialog.tsx